import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { toast } from "@gigpro/ui/Toast";
import { ApiError } from "@sidegigllc/api";
import type { JobPosting } from "@sidegigllc/api/client";
import { usePublishJob } from "api/queries/hire";
import React from "react";
import { toastApiError } from "utils/toastApiError";
import * as z from "zod/mini";

type PostPubliclyJob = Pick<JobPosting, "id" | "name" | "status"> &
  Partial<Pick<JobPosting, "is_private" | "origin" | "stats">>;

/** A direct-hire job that is still private, or was closed without anyone being hired, can be posted publicly. */
export const canPostPublicly = (job: Omit<PostPubliclyJob, "id" | "name">) => {
  if (job.origin !== "direct_hire") return false;
  if (job.status === "open") return !!job.is_private;
  return job.status === "archived" && job.stats?.offered_count === 0;
};

const schema = z.object({
  name: z.string().check(z.trim(), z.minLength(1, "Enter a job title"), z.maxLength(50, "Keep it to 50 characters")),
});

type PostPubliclyButtonProps = {
  job: PostPubliclyJob;
  onPublished?: () => void;
  className?: string;
};

/** "Post publicly to find more people". If the title collides with an open job here, it asks for a new one. */
export const PostPubliclyButton: React.FC<PostPubliclyButtonProps> = ({ job, onPublished, className }) => {
  const [renaming, setRenaming] = React.useState(false);
  const form = useZodForm({ schema, defaultValues: { name: job.name } });
  const { mutateAsync: publish, isPending } = usePublishJob();

  const post = async (name?: string) => {
    try {
      await publish({ id: job.id, data: name ? { name } : undefined });
      setRenaming(false);
      toast.success("Your job is now posted publicly");
      onPublished?.();
    } catch (error) {
      const apiError = ApiError.fromThrown(error);
      if (apiError.hasFieldError("name")) {
        form.setError("name", { message: apiError.getFieldErrorMessage("name") });
        setRenaming(true);
        return;
      }
      toastApiError(error);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        className={className}
        loading={isPending && !renaming}
        disabled={isPending}
        // The list row is a link, so keep the click from opening the job.
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void post();
        }}
      >
        Post publicly to find more people
      </Button>

      <Dialog bottomSheet blockInteraction open={renaming} onClose={() => setRenaming(false)}>
        <DialogTitle>Pick a new job title</DialogTitle>
        <p className="text-normal text-black">
          You already have an open job with this title. Give this one a new title.
        </p>
        <Form form={form} onSubmit={({ name }) => post(name)} className="flex flex-col gap-2">
          <Textfield control={form.control} label="Job title" name="name" maxLength={50} />
          <DialogFooter>
            <Button
              type="button"
              className="w-full"
              onClick={() => setRenaming(false)}
              disabled={form.formState.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="w-full"
              loading={form.formState.isSubmitting}
              disabled={form.formState.isSubmitting}
            >
              Post publicly
            </Button>
          </DialogFooter>
        </Form>
      </Dialog>
    </>
  );
};
